import { Star } from "lucide-react";
import { cn } from "../../lib/utils";
import { CommandGroup, CommandItem } from "./command";
import { ShopLogo, Shop } from "./shop-logo";

/** A star on each row, to pin shops to the top of the switcher. */
export interface ShopBookmark {
  /** `undefined` when the shop cannot be pinned (e.g. one's own shop). */
  isBookmarked: (shop: Shop) => boolean | undefined;
  onToggle: (shop: Shop, isBookmarked: boolean) => void;
  addLabel: string;
  removeLabel: string;
}

export interface ShopsListProps {
  shops: Shop[];
  currentShop: Shop;
  onShopSelect: (shop: Shop) => void;
  heading?: string;
  bookmark?: ShopBookmark;
}

export default function ShopsList({
  shops,
  currentShop,
  onShopSelect,
  heading = "Dashboard",
  bookmark,
}: ShopsListProps) {
  if (!currentShop || !shops) return null;

  return (
    <CommandGroup heading={heading}>
      {shops.map((shop) => (
        <CommandItem
          key={shop.id}
          value={`${shop.name}|${shop.shopifyUrl ?? ""}|${shop.id}`}
          onSelect={() => {
            if (shop.id !== currentShop.id) {
              onShopSelect(shop);
            }
          }}
          className={cn(
            "group cursor-pointer text-sm gap-2 text-muted-forground font-medium hover:bg-accent hover:font-semibold",
            currentShop.id === shop.id ? "bg-accent font-semibold" : "",
          )}
        >
          <ShopLogo shop={shop} />
          <span className="flex-1 min-w-0">
            <span className="block truncate">{shop.name}</span>
            {shop.shopifyUrl && (
              <span className="block truncate text-xs font-normal text-muted-foreground">
                {shop.shopifyUrl}
              </span>
            )}
          </span>
          {shop.isActive === false && (
            <span
              className="h-2 w-2 rounded-full bg-red-400 shrink-0"
              title="Shop inactif"
            />
          )}
          {bookmark && <BookmarkButton shop={shop} bookmark={bookmark} />}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function BookmarkButton({
  shop,
  bookmark,
}: {
  shop: Shop;
  bookmark: ShopBookmark;
}) {
  const isBookmarked = bookmark.isBookmarked(shop);
  if (isBookmarked === undefined) return null;

  const label = isBookmarked ? bookmark.removeLabel : bookmark.addLabel;

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isBookmarked}
      title={label}
      className={cn(
        "shrink-0 rounded p-1 text-muted-foreground hover:text-amber-500",
        // Pinned shops always show their star; the others on the active row.
        !isBookmarked &&
          "opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100 focus-visible:opacity-100",
      )}
      // The row's click selects the shop: the star only toggles the pin.
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        bookmark.onToggle(shop, !isBookmarked);
      }}
    >
      <Star
        className={cn(
          "h-4 w-4",
          isBookmarked && "fill-amber-400 text-amber-500",
        )}
      />
    </button>
  );
}
